import React, { useState, useEffect, useRef } from 'react'

// Hook para detectar gestos touch
export const useTouchGestures = () => {
  const [gestures, setGestures] = useState({
    swipeLeft: false,
    swipeRight: false,
    swipeUp: false,
    swipeDown: false,
    longPress: false,
    doubleTap: false
  })

  const touchStart = useRef({ x: 0, y: 0, time: 0 })
  const touchEnd = useRef({ x: 0, y: 0, time: 0 })
  const longPressTimer = useRef(null)

  const handleTouchStart = (e) => {
    const touch = e.touches[0]
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    }

    // Long press detection
    longPressTimer.current = setTimeout(() => {
      setGestures(prev => ({ ...prev, longPress: true }))
    }, 500)
  }

  const handleTouchEnd = (e) => {
    const touch = e.changedTouches[0]
    touchEnd.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    }

    // Clear long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }

    // Calculate swipe direction
    const deltaX = touchEnd.current.x - touchStart.current.x
    const deltaY = touchEnd.current.y - touchStart.current.y
    const deltaTime = touchEnd.current.time - touchStart.current.time

    const minSwipeDistance = 50
    const maxSwipeTime = 300

    if (deltaTime < maxSwipeTime) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (Math.abs(deltaX) > minSwipeDistance) {
          setGestures(prev => ({
            ...prev,
            swipeLeft: deltaX < 0,
            swipeRight: deltaX > 0
          }))
        }
      } else {
        if (Math.abs(deltaY) > minSwipeDistance) {
          setGestures(prev => ({
            ...prev,
            swipeUp: deltaY < 0,
            swipeDown: deltaY > 0
          }))
        }
      }
    }

    // Reset gestures after a short delay
    setTimeout(() => {
      setGestures({
        swipeLeft: false,
        swipeRight: false,
        swipeUp: false,
        swipeDown: false,
        longPress: false,
        doubleTap: false
      })
    }, 100)
  }

  return {
    gestures,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd
    }
  }
}

// Hook para otimização mobile
export const useMobileOptimization = () => {
  const [isMobile, setIsMobile] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const [viewportHeight, setViewportHeight] = useState(0)

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      const height = window.innerHeight

      setIsMobile(mobile)
      setIsTouchDevice(touch)
      setViewportHeight(height)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    window.addEventListener('orientationchange', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
      window.removeEventListener('orientationchange', checkMobile)
    }
  }, [])

  return {
    isMobile,
    isTouchDevice,
    viewportHeight,
    // Touch-friendly dimensions
    touchTargetSize: 44, // Minimum touch target size
    safeAreaTop: window.safeAreaInsets?.top || 0,
    safeAreaBottom: window.safeAreaInsets?.bottom || 0
  }
}

// Componente de botão otimizado para mobile
export const MobileButton = ({ 
  children, 
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  onPress,
  className = '',
  ...props 
}) => {
  const { isMobile, isTouchDevice, touchTargetSize } = useMobileOptimization()
  const { gestures, touchHandlers } = useTouchGestures()

  const sizeClasses = {
    sm: `h-10 px-3 text-sm`,
    md: `h-12 px-4 text-base`,
    lg: `h-14 px-6 text-lg`
  }

  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 active:bg-gray-800',
    danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
    success: 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800',
    outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 active:bg-blue-100'
  }

  const buttonClasses = `
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${fullWidth || isMobile ? 'w-full' : 'w-auto'}
    ${isTouchDevice ? `min-h-[${touchTargetSize}px]` : ''}
    rounded-lg font-medium transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    active:scale-95
    ${className}
  `

  const handleClick = () => {
    if (!disabled && !loading && onPress) {
      onPress()
    }
  }

  return (
    <button
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || loading}
      {...touchHandlers}
      {...props}
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Carregando...</span>
        </div>
      ) : (
        children
      )}
    </button>
  )
}

// Componente de input otimizado para mobile
export const MobileInput = ({ 
  type = 'text',
  placeholder = '',
  value,
  onChange,
  fullWidth = true,
  disabled = false,
  error = false,
  className = '',
  ...props 
}) => {
  const { isMobile, isTouchDevice, touchTargetSize } = useMobileOptimization()

  const inputClasses = `
    ${fullWidth ? 'w-full' : 'w-auto'}
    ${isTouchDevice ? `min-h-[${touchTargetSize}px]` : 'h-10'}
    px-3 py-2 border rounded-lg
    ${error ? 'border-red-500' : 'border-gray-300'}
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
    text-base sm:text-lg
    disabled:opacity-50 disabled:cursor-not-allowed
    ${className}
  `

  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={inputClasses}
      {...props}
    />
  )
}

// Componente de card otimizado para mobile
export const MobileCard = ({ 
  children, 
  className = '',
  padding = 'md',
  shadow = true,
  interactive = false,
  onPress,
  ...props 
}) => {
  const { isMobile, isTouchDevice } = useMobileOptimization()
  const { gestures, touchHandlers } = useTouchGestures()

  const paddingClasses = {
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6'
  }

  const cardClasses = `
    bg-white rounded-xl
    ${paddingClasses[padding]}
    ${shadow ? 'shadow-lg' : 'shadow-sm'}
    ${interactive ? 'cursor-pointer active:scale-95 transition-transform' : ''}
    ${className}
  `

  const handlePress = () => {
    if (interactive && onPress) {
      onPress()
    }
  }

  return (
    <div
      className={cardClasses}
      onClick={handlePress}
      {...(interactive ? touchHandlers : {})}
      {...props}
    >
      {children}
    </div>
  )
}

// Hook para navegação mobile
export const useMobileNavigation = () => {
  const [currentScreen, setCurrentScreen] = useState('dashboard')
  const [navigationHistory, setNavigationHistory] = useState(['dashboard'])
  const { isMobile } = useMobileOptimization()

  const navigateTo = (screen) => {
    setCurrentScreen(screen)
    setNavigationHistory(prev => [...prev, screen])
  }

  const goBack = () => {
    if (navigationHistory.length > 1) {
      const newHistory = navigationHistory.slice(0, -1)
      setNavigationHistory(newHistory)
      setCurrentScreen(newHistory[newHistory.length - 1])
    }
  }

  const canGoBack = navigationHistory.length > 1

  return {
    currentScreen,
    navigateTo,
    goBack,
    canGoBack,
    isMobile
  }
}

// Componente de lista otimizada para mobile
export const MobileList = ({ 
  items, 
  renderItem, 
  keyExtractor,
  onItemPress,
  className = '',
  ...props 
}) => {
  const { isMobile, isTouchDevice } = useMobileOptimization()

  return (
    <div className={`space-y-2 ${className}`} {...props}>
      {items.map((item, index) => (
        <div
          key={keyExtractor ? keyExtractor(item, index) : index}
          className={`
            ${isTouchDevice ? 'min-h-[44px]' : ''}
            ${onItemPress ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100' : ''}
            transition-colors duration-200
          `}
          onClick={() => onItemPress && onItemPress(item, index)}
        >
          {renderItem(item, index)}
        </div>
      ))}
    </div>
  )
}

// Hook para performance mobile
export const useMobilePerformance = () => {
  const [isSlowConnection, setIsSlowConnection] = useState(false)
  const [isLowMemory, setIsLowMemory] = useState(false)

  useEffect(() => {
    // Detectar conexão lenta
    if ('connection' in navigator) {
      const connection = navigator.connection
      setIsSlowConnection(connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')
    }

    // Detectar memória baixa
    if ('deviceMemory' in navigator) {
      setIsLowMemory(navigator.deviceMemory < 4)
    }
  }, [])

  return {
    isSlowConnection,
    isLowMemory,
    shouldOptimizeImages: isSlowConnection || isLowMemory,
    shouldLazyLoad: isSlowConnection,
    shouldReduceAnimations: isLowMemory
  }
}

// Componente de loading otimizado para mobile
export const MobileLoading = ({ 
  size = 'md',
  text = 'Carregando...',
  className = ''
}) => {
  const { isMobile } = useMobileOptimization()

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  }

  return (
    <div className={`flex flex-col items-center justify-center p-4 ${className}`}>
      <div className={`${sizeClasses[size]} border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-2`}></div>
      {text && (
        <p className={`text-gray-600 ${isMobile ? 'text-sm' : 'text-base'}`}>
          {text}
        </p>
      )}
    </div>
  )
}

export default {
  useTouchGestures,
  useMobileOptimization,
  useMobileNavigation,
  useMobilePerformance,
  MobileButton,
  MobileInput,
  MobileCard,
  MobileList,
  MobileLoading
}
